import { Link } from "react-router-dom";
import { IMAGES, CATEGORIES } from "@/data/site";
import { PageHero } from "@/components/PageHero";
import { FadeUp, Eyebrow } from "@/components/Reveal";
import { Photo } from "@/components/Photo";
import { Seo } from "@/components/Seo";
import { HistoryTeaser, VisitBand } from "@/components/TobagoHistory";
import { EstateProjects } from "@/components/EstateProjects";

const LANDSCAPE = [
  { t: "The Coastline", d: "Where the estate meets the Caribbean Sea and the bay that gives it its name." },
  { t: "The Rainforest", d: "Dense tropical forest, rich in vegetation and wildlife, rising behind the shore." },
  { t: "The Rivers", d: "Fresh water moving through the forest and across the land." },
  { t: "The Waterfalls", d: "Places where the rivers fall through the rainforest on their way to the lowlands." },
  { t: "The Agricultural Land", d: "Cultivated ground, including the estate's cocoa heritage." },
];

const Section = ({ numeral, eyebrow, title, children, image, alt, reverse, testid }) => (
  <section className="mx-auto max-w-[1440px] px-5 py-20 md:px-10 lg:py-24" data-testid={testid}>
    <div className={`grid items-center gap-14 lg:grid-cols-12 ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
      <FadeUp className="lg:col-span-5">
        <Photo image={image} alt={alt} className="aspect-[4/5] rounded-t-[999px] lg:aspect-auto lg:h-[520px]" />
      </FadeUp>
      <div className={`lg:col-span-6 ${reverse ? "" : "lg:col-start-7"}`}>
        <FadeUp>
          <p className="font-display text-7xl text-gold/70">{numeral}</p>
          <Eyebrow className="mt-4">{eyebrow}</Eyebrow>
          <h2 className="mt-5 font-display text-4xl leading-[1.05] text-heritage md:text-6xl">{title}</h2>
          <div className="mt-8 space-y-5 text-lg leading-relaxed text-muted-ink">{children}</div>
        </FadeUp>
      </div>
    </div>
  </section>
);

export default function Estate() {
  return (
    <>
      <Seo
        title="The Estate"
        path="/estate"
        description="The history of Tobago, the island that changed hands more than thirty times, and the heritage, landscape and vision of Little Englishman's Bay Estate, Est. 1777."
      />
      <PageHero eyebrow="The Estate · Est. 1777" lines={["A heritage estate,", "a living landscape."]} image={{ src: "/images/estate-bay-poster.webp", video: [{ src: "/images/estate-bay.webm", type: "video/webm" }, { src: "/images/estate-bay.mp4", type: "video/mp4" }] }} alt="Tobago bay with a sailboat on turquoise water, framed by rainforest-covered hills" testid="estate-hero" softFade />

      <Section numeral="I" eyebrow="Heritage" title="The Estate's Heritage" image={{ src: "/images/heritage-creek.webp", video: [{ src: "/images/heritage-creek.webm", type: "video/webm" }, { src: "/images/heritage-creek.mp4", type: "video/mp4" }] }} alt="Cascading rainforest creek beside an old moss-covered stone arch" testid="estate-heritage">
        <p>
          Little Englishman's Bay Estate was established in 1777. That was the year Britain's cannon were hauled up to Fort King George, and the year after Tobago's rainforest was placed under protection. The estate was born in the most dramatic chapter of the island's story.
        </p>
        <p>
          Its lands hold, in one place, everything that made Tobago worth fighting for: fresh water running down from the forest, cocoa growing in the shade, a sheltered bay, and the wild abundance of the coast and rainforest.
        </p>
        <p>
          Flags changed and empires came and went, and the landscape endured. Today that heritage is carried in the estate's crest, the crown, the palm and the words <em>Est. 1777</em>, and in the care with which its land is now being shared.
        </p>
      </Section>

      <section className="bg-heritage py-20 text-ivory lg:py-24" data-testid="estate-landscape">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          <div className="grid gap-12 lg:grid-cols-12">
            <FadeUp className="lg:col-span-5">
              <p className="font-display text-7xl text-gold/70">II</p>
              <p className="mt-4 font-caps text-[12px] tracking-[0.32em] uppercase text-gold">The Natural Landscape</p>
              <h2 className="mt-5 font-display text-4xl leading-[1.05] md:text-6xl">One landscape, <em className="text-gold-light">from forest to sea.</em></h2>
              <p className="mt-8 max-w-md text-lg leading-relaxed text-ivory/80">
                The estate's character comes from how its parts belong together. Rainforest gives rise to rivers and waterfalls, fresh water sustains cultivated land, and all of it opens onto the coast.
              </p>
            </FadeUp>
            <ol className="lg:col-span-6 lg:col-start-7">
              {LANDSCAPE.map((l, i) => (
                <FadeUp key={l.t} delay={i * 0.06} y={18}>
                  <li className="grid grid-cols-[3rem_1fr] gap-4 border-b border-gold/25 py-5" data-testid={`landscape-item-${i}`}>
                    <span className="font-caps text-sm text-gold">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <h3 className="font-display text-3xl">{l.t}</h3>
                      <p className="mt-2 text-ivory/75">{l.d}</p>
                    </div>
                  </li>
                </FadeUp>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <EstateProjects />
      <HistoryTeaser />

      <Section numeral="III" eyebrow="Vision" title="The Estate's Vision" image={IMAGES.rainbow} alt="Rainbow over green hills and the sea in Trinidad and Tobago" reverse testid="estate-vision">
        <p>
          The estate looks ahead through a growing collection of hospitality, conservation, agricultural and tourism projects, each one shaped by a part of its landscape and intended to work with the land.
        </p>
        <p>These projects are gathered into six areas:</p>
        <ul className="flex flex-wrap gap-3 pt-2">
          {CATEGORIES.map((c) => (
            <li key={c.slug}>
              <Link
                to={`/${c.slug}`}
                data-testid={`vision-cat-${c.slug}`}
                className="inline-block rounded-full border border-gold px-4 py-2 font-caps text-[11px] tracking-[0.18em] uppercase text-heritage transition-colors duration-300 hover:bg-heritage hover:text-ivory"
              >
                {c.title}
              </Link>
            </li>
          ))}
        </ul>
      </Section>
      <VisitBand />
    </>
  );
}
